import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sprout, Leaf, Apple, Dumbbell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/data/site";
import { Seo } from "@/components/Seo";

const MY_PLANS = [
  { icon: Sprout, title: "Detoxplan", meta: "Detox", progress: 0 },
  { icon: Leaf, title: "Livsstilsplan", meta: "Kosthold + barn", progress: 0 },
  { icon: Dumbbell, title: "Booty & Hourglass", meta: "Trening", progress: 0 },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);
  if (!user) return <div className="min-h-screen grid place-items-center bg-cream text-olive/60">Laster…</div>;

  return (
    <div className="min-h-screen bg-cream grain">
      <Seo title="Min side – Helserebellen" description="Dine programmer og din fremgang." path="/dashboard" noindex />
      <header className="border-b border-olive/12 py-5 px-5 md:px-10">
        <div className="mx-auto max-w-6xl flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="" className="h-10 w-10 object-contain mix-blend-multiply" />
            <span className="font-serif text-lg text-olive-deep">Helserebellen</span>
          </Link>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="dashboard-logout"
            className="text-[12px] uppercase tracking-[0.16em] text-olive/60 hover:text-terra">Logg ut</button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 md:px-10 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-script text-4xl text-terra">Hei, {user.name?.split(" ")[0] || "rebell"} 🌿</p>
          <h1 className="mt-2 font-serif text-5xl md:text-6xl text-olive-deep font-light">Min side</h1>
          <p className="mt-3 text-olive/65 max-w-lg">Her samles programmene dine. Velg en plan for å komme i gang på egne premisser.</p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          {MY_PLANS.map((p, i) => (
            <motion.div key={p.title} data-testid={`dashboard-plan-${i}`}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.08 }}
              className="rounded-[24px] border border-olive/12 bg-white/60 p-8 hover:-translate-y-1 transition-transform duration-500">
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sage/25 text-olive-deep">
                  <p.icon size={26} strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="font-serif text-2xl text-olive-deep">{p.title}</h3>
                  <p className="text-[12px] uppercase tracking-[0.16em] text-olive/50">{p.meta}</p>
                </div>
              </div>
              <div className="mt-6 h-1.5 rounded-full bg-olive/10 overflow-hidden">
                <div className="h-full bg-terra" style={{ width: `${p.progress}%` }} />
              </div>
              <button className="mt-6 w-full rounded-full bg-olive-deep px-6 py-3 text-cream text-[12px] uppercase tracking-[0.16em] hover:bg-terra transition-colors">
                Start programmet
              </button>
            </motion.div>
          ))}
        </div>

        <Link to="/" className="mt-12 inline-flex items-center gap-2 text-olive/60 hover:text-terra"><ArrowLeft size={16} /> Tilbake til forsiden</Link>
      </main>
    </div>
  );
}
